/**
 * Planet hazard models, second pass: volcanic vent, geyser field, crystal
 * formation. Built at unit scale (the renderer scales the group by the hazard's
 * size and the planet's prop scale), Y up from the surface, deterministic from
 * the hazard id so every peer sees the same formation.
 *
 * Returns the handles the animation loop needs: the lava/mineral pool (opacity
 * pulse), the crystal meshes (three shared shimmer materials per formation, so
 * they merge into three draws), one InstancedMesh of smoke/steam wisps and the
 * height they respawn at. No point lights: emissive + bloom carries the glow,
 * and every point light in the scene costs every lit fragment a loop iteration.
 *
 * Design reference: design_sheets/scenery.html (concepts from planet_hazards.html)
 */

import * as THREE from 'three';
import type { HazardType } from '../types';
import { KitRng, mat, box, cyl, sphere, torus, at } from './buildingKit';

export interface HazardModel {
  group: THREE.Group;
  pool: THREE.Mesh | null;
  /** Crystal spires + splinters (pre-bake handles; they share `crystalMats`). */
  crystals: THREE.Mesh[];
  crystalMats: THREE.MeshLambertMaterial[];
  /** Smoke / steam puffs as one InstancedMesh (null for crystal formations). */
  wisps: THREE.InstancedMesh | null;
  wispRadius: number;
  /** Local height the wisps respawn at (vent / nozzle mouth). */
  ventY: number;
}

const flat = (c: string) => new THREE.MeshLambertMaterial({ color: new THREE.Color(c), flatShading: true });
const glowT = (c: string, i: number, opacity: number) => new THREE.MeshLambertMaterial({ color: new THREE.Color(c), emissive: new THREE.Color(c), emissiveIntensity: i, transparent: true, opacity });
const shade = (c: string, opacity: number) => new THREE.MeshLambertMaterial({ color: new THREE.Color(c), transparent: true, opacity, depthWrite: false });

/** Seam-safe radial lumps: a sum of sines in the vertex's angle and height. */
function lumpy(geo: THREE.BufferGeometry, rng: KitRng, amount: number): void {
  const p = geo.attributes.position as THREE.BufferAttribute;
  const ph = [rng.range(0, 6.28), rng.range(0, 6.28), rng.range(0, 6.28)];
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i), y = p.getY(i), z = p.getZ(i);
    const r = Math.hypot(x, z); if (r < 1e-4) continue;
    const a = Math.atan2(z, x);
    const n = Math.sin(a * 3 + ph[0] + y * 2) * 0.5 + Math.sin(a * 5 + ph[1] - y) * 0.3 + Math.sin(a * 8 + ph[2]) * 0.2;
    const k = 1 + n * amount;
    p.setXYZ(i, x * k, y, z * k);
  }
  p.needsUpdate = true;
  geo.computeVertexNormals();
}

/** n puffs in one draw: unit sphere instances, scaled per particle by the update loop. */
function wisps(n: number, color: string, opacity: number): THREE.InstancedMesh {
  const m = new THREE.InstancedMesh(new THREE.SphereGeometry(1, 5, 4), new THREE.MeshBasicMaterial({ color, transparent: true, opacity, depthWrite: false }), n);
  m.name = 'hazard:wisps';
  const zero = new THREE.Matrix4().makeScale(0, 0, 0);
  for (let i = 0; i < n; i++) m.setMatrixAt(i, zero);
  m.instanceMatrix.needsUpdate = true;
  m.frustumCulled = false;
  m.userData.noBake = true;
  return m;
}

function volcanic(rng: KitRng): HazardModel {
  const g = new THREE.Group();
  const flankGeo = new THREE.CylinderGeometry(0.2, 0.78, 0.6, 12, 4);
  lumpy(flankGeo, rng, 0.08);
  g.add(at(new THREE.Mesh(flankGeo, flat('#4a3028')), 0, 0.3, 0));
  g.add(at(torus(0.21, 0.04, flat('#3a2820'), 6, 12), 0, 0.6, 0, Math.PI / 2));                      // crust rim
  const pool = at(new THREE.Mesh(new THREE.CircleGeometry(0.17, 10), glowT('#ff6a30', 0.8, 0.6)), 0, 0.605, 0, -Math.PI / 2);
  g.add(pool);
  for (let i = 0; i < 3; i++) {                                                                        // cooling lava channels down the flank
    const a = rng.range(0, Math.PI * 2), len = rng.range(0.2, 0.32);
    const ch = at(box(0.03, 0.006, len, glowT('#ff5020', 0.6, 0.9)), Math.cos(a) * 0.42, 0.34, Math.sin(a) * 0.42, 0, -a + Math.PI / 2, 0);
    ch.rotateX(-0.82);
    g.add(ch);
  }
  g.add(at(new THREE.Mesh(new THREE.CircleGeometry(0.95, 18), shade('#2a2020', 0.35)), 0, 0.003, 0, -Math.PI / 2));   // ash fall
  for (let i = 0; i < 6; i++) {                                                                        // ejected blocks
    const a = rng.range(0, Math.PI * 2), d = rng.range(0.72, 0.98), r = rng.range(0.05, 0.1);
    g.add(at(new THREE.Mesh(i % 2 ? new THREE.TetrahedronGeometry(r, 0) : new THREE.DodecahedronGeometry(r, 0), flat(i % 3 ? '#3a2820' : '#2a1c18')), Math.cos(a) * d, r, Math.sin(a) * d, rng.range(0, 3), rng.range(0, 3), 0));
  }
  const sa = rng.range(0, Math.PI * 2);                                                                // secondary vent
  g.add(at(cyl(0.06, 0.22, 0.2, flat('#4a3028'), 8), Math.cos(sa) * 0.62, 0.1, Math.sin(sa) * 0.62), at(new THREE.Mesh(new THREE.CircleGeometry(0.045, 8), glowT('#ff6a30', 0.7, 0.8)), Math.cos(sa) * 0.62, 0.202, Math.sin(sa) * 0.62, -Math.PI / 2));
  const smoke = wisps(8, '#808080', 0.35);
  g.add(smoke);
  return { group: g, pool, crystals: [], crystalMats: [], wisps: smoke, wispRadius: 0.06, ventY: 0.6 };
}

function geyser(rng: KitRng): HazardModel {
  const g = new THREE.Group();
  const tiers: Array<[number, number, string, string]> = [[0.62, 0.04, '#6a7a70', '#8a9a90'], [0.44, 0.05, '#7a8a80', '#9aaaa0'], [0.27, 0.06, '#8a9a90', '#aabab0']];
  let y = 0;
  for (const [r, h, c, lip] of tiers) {                                                                 // mineral terraces
    const geo = new THREE.CylinderGeometry(r, r * 1.04, h, 14, 1); lumpy(geo, rng, 0.05);
    g.add(at(new THREE.Mesh(geo, flat(c)), 0, y + h / 2, 0), at(torus(r, 0.012, mat(lip), 5, 14), 0, y + h, 0, Math.PI / 2));
    y += h;
  }
  const pool = at(new THREE.Mesh(new THREE.CircleGeometry(0.4, 14), glowT('#2e7878', 0.25, 0.5)), 0, tiers[0][1] + tiers[1][1] + 0.002, 0, -Math.PI / 2);
  g.add(pool);                                                                                          // standing mineral water on the middle terrace
  const moundGeo = new THREE.CylinderGeometry(0.1, 0.2, 0.12, 8, 2); lumpy(moundGeo, rng, 0.06);
  g.add(at(new THREE.Mesh(moundGeo, flat('#4a5a50')), 0, y + 0.06, 0));
  g.add(at(torus(0.09, 0.025, flat('#3a4a44'), 6, 10), 0, y + 0.12, 0, Math.PI / 2), at(new THREE.Mesh(new THREE.CircleGeometry(0.07, 10), mat('#101818')), 0, y + 0.121, 0, -Math.PI / 2));   // nozzle + throat
  for (let i = 0; i < 5; i++) {                                                                         // wet rocks
    const a = rng.range(0, Math.PI * 2), d = rng.range(0.46, 0.68), r = rng.range(0.05, 0.08);
    const rock = sphere(r, flat(i % 2 ? '#5a6a58' : '#607060'), 6, 4); rock.scale.y = 0.6;
    g.add(at(rock, Math.cos(a) * d, r * 0.5, Math.sin(a) * d, 0, rng.range(0, 3), 0));
  }
  for (let i = 0; i < 4; i++) { const a = rng.range(0, Math.PI * 2); g.add(at(box(0.04, 0.004, 0.3, mat('#c8d0c0')), Math.cos(a) * 0.38, tiers[0][1] + 0.004, Math.sin(a) * 0.38, 0, -a + Math.PI / 2, 0)); }   // mineral staining
  for (let i = 0; i < 2; i++) { const a = rng.range(0, Math.PI * 2); g.add(at(torus(0.04, 0.012, flat('#3a4a44'), 5, 8), Math.cos(a) * 0.52, tiers[0][1] + 0.01, Math.sin(a) * 0.52, Math.PI / 2), at(new THREE.Mesh(new THREE.CircleGeometry(0.03, 8), mat('#101818')), Math.cos(a) * 0.52, tiers[0][1] + 0.012, Math.sin(a) * 0.52, -Math.PI / 2)); }   // side vents
  const ventY = y + 0.12;
  const steam = wisps(6, '#a0b8b8', 0.3);
  g.add(steam);
  return { group: g, pool, crystals: [], crystalMats: [], wisps: steam, wispRadius: 0.05, ventY };
}

function crystal(rng: KitRng): HazardModel {
  const g = new THREE.Group();
  const palette = ['#80a0c8', '#7090b8', '#90b0d8', '#6888b0', '#a0c0e0', '#7898c0'];
  const baseGeo = new THREE.IcosahedronGeometry(0.36, 1); lumpy(baseGeo, rng, 0.1);
  const base = new THREE.Mesh(baseGeo, flat('#2a2a38')); base.scale.y = 0.3; base.position.y = 0.11; g.add(base);   // 0.36 × 0.3 half-height: sits on the ground
  const crystals: THREE.Mesh[] = [];
  // Three shared materials (three shimmer phases): the whole formation bakes to three draws
  const crystalMats = [palette[0], palette[2], palette[4]].map(c => glowT(c, 0.35, 0.8));
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2 + rng.range(-0.2, 0.2), d = rng.range(0.08, 0.42);
    const h = rng.range(0.22, 0.62), w = rng.range(0.04, 0.1);
    const m = crystalMats[i % 3];
    const spire = new THREE.Group();
    const body = at(new THREE.Mesh(new THREE.CylinderGeometry(w * 0.85, w, h * 0.65, 6), m), 0, h * 0.325, 0);
    const tip = at(new THREE.Mesh(new THREE.ConeGeometry(w * 0.85, h * 0.45, 6), m), 0, h * 0.65 + h * 0.225, 0);
    spire.add(body, tip); crystals.push(body, tip);
    spire.position.set(Math.cos(a) * d, 0.02, Math.sin(a) * d);
    spire.rotation.set(rng.range(-0.3, 0.3), rng.range(0, Math.PI), rng.range(-0.3, 0.3));
    g.add(spire);
  }
  for (let i = 0; i < 6; i++) {                                                                        // splinters at the edge
    const a = rng.range(0, Math.PI * 2), d = rng.range(0.44, 0.58), h = rng.range(0.06, 0.12);
    const sp = at(new THREE.Mesh(new THREE.ConeGeometry(0.02, h, 5), crystalMats[i % 3]), Math.cos(a) * d, h / 2 - 0.01, Math.sin(a) * d, rng.range(-0.5, 0.5), 0, rng.range(-0.5, 0.5));
    g.add(sp); crystals.push(sp);
  }
  for (let i = 0; i < 5; i++) {                                                                        // frost-shattered rock around the base
    const a = rng.range(0, Math.PI * 2), d = rng.range(0.3, 0.5), r = rng.range(0.04, 0.09);
    g.add(at(new THREE.Mesh(new THREE.DodecahedronGeometry(r, 0), flat(i % 2 ? '#2a2a38' : '#343446')), Math.cos(a) * d, r * 0.8, Math.sin(a) * d, rng.range(0, 3), rng.range(0, 3), 0));
  }
  g.add(at(new THREE.Mesh(new THREE.CircleGeometry(0.62, 16), shade('#405068', 0.25)), 0, 0.003, 0, -Math.PI / 2));   // cold light pooling on the ground
  return { group: g, pool: null, crystals, crystalMats, wisps: null, wispRadius: 0, ventY: 0.3 };
}

export function buildHazardModel(type: HazardType, seed: number): HazardModel {
  const rng = new KitRng(7919 + seed * 131);
  switch (type) {
    case 'volcanic': return volcanic(rng);
    case 'geyser': return geyser(rng);
    default: return crystal(rng);
  }
}
